import {
  SalesforceSyncOption,
  SalesforceSyncTypes,
} from 'module/api/integrations/salesforce/types';
import {
  Button,
  PopoverSelect,
  type PopoverSelectOption,
} from '@rocketlane/ui';
import { useIntl } from 'react-intl';
import { SyncIcon } from './sync-utils';

interface SyncDirectionSelectProps {
  disabled?: boolean;
  isLoading?: boolean;
  onChange: (value: SalesforceSyncTypes) => void;
  options: SalesforceSyncOption[];
  value: SalesforceSyncTypes;
}

interface SyncDirectionOption extends PopoverSelectOption {
  value: SalesforceSyncTypes;
}

const isSyncDirection = (
  value: string | number,
): value is SalesforceSyncTypes =>
  Object.values(SalesforceSyncTypes).some((candidate) => candidate === value);

export const SyncDirectionSelect = ({
  disabled,
  isLoading,
  onChange,
  options,
  value,
}: SyncDirectionSelectProps) => {
  const intl = useIntl();
  const selectOptions: SyncDirectionOption[] = options.map((option) => ({
    value: option.value,
    label: intl.formatMessage({
      id: `settings.integrations.sf.resource_mappings.sync.${option.label.toLowerCase()}`,
    }),
  }));
  const selected =
    selectOptions.find((option) => option.value === value) ?? null;

  return (
    <PopoverSelect
      dropdownWidth={200}
      isLoading={isLoading}
      options={selectOptions}
      value={selected}
      onChange={(option) => {
        if (
          option != null &&
          !Array.isArray(option) &&
          isSyncDirection(option.value)
        ) {
          onChange(option.value);
        }
      }}
      renderOptionIcon={(option) =>
        isSyncDirection(option.value) ? <SyncIcon value={option.value} /> : null
      }
      renderTrigger={({ value: triggerValue }) => {
        const triggerDirection =
          triggerValue != null &&
          !Array.isArray(triggerValue) &&
          isSyncDirection(triggerValue.value)
            ? triggerValue.value
            : value;

        return (
          <Button
            aria-label={selected?.label ?? 'Select sync direction'}
            data-cy="salesforce-sync-direction"
            disabled={disabled}
            leftIcon={<SyncIcon value={triggerDirection} />}
            loading={isLoading}
            size="small"
            type="button"
            variant="icon"
          />
        );
      }}
      searchable={false}
      showFooter={false}
    />
  );
};
